import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import GenericTable from "../../../Components/Ui_elements/GenericTable";
import PopupModal from "../../../Components/Ui_elements/PopupModal";
import Btn from "../../../Components/Ui_elements/Btn";
import DropdownComponent from "../../../Components/DropdownComponent";
import authApi from "../../../redux/authApi";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/Slice/snackbar/snackbarSlice";
import { useHandleSmartNavigation } from "../../../Helper/handleSmartNavigation";
import { formatDate, localizeNumber } from "../../../Helper/generalhelper";

const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const formatQty = value => {
  if (value === null || value === undefined || value === "") return "-";
  return localizeNumber(value);
};

const formatRate = value => {
  if (value === null || value === undefined || value === "") return "-";
  return localizeNumber(value, 4);
};

const statusChip = value => {
  const statusMap = {
    pending: { label: "Pending", color: "warning" },
    approved: { label: "Approved", color: "success" },
    rejected: { label: "Rejected", color: "error" },
  };
  const s = statusMap[value] || { label: value, color: "default" };
  return <Chip size="small" label={s.label} color={s.color} variant="outlined" />;
};

const SummaryBlock = ({ summary }) => {
  if (!summary) return "-";
  return (
    <Box>
      <Typography variant="body2">Changed rows: {summary.changedRowsCount || 0}</Typography>
      <Typography variant="body2">
        Added languages: {summary.addedLanguagePairsCount || 0}
      </Typography>
      <Typography variant="body2">
        Added services: {summary.addedServicesCount || 0}
      </Typography>
      {(summary.removedLanguagePairsCount || 0) > 0 && (
        <Typography variant="body2">
          Removed languages: {summary.removedLanguagePairsCount || 0}
        </Typography>
      )}
      {(summary.removedServicesCount || 0) > 0 && (
        <Typography variant="body2">
          Removed services: {summary.removedServicesCount || 0}
        </Typography>
      )}
    </Box>
  );
};

const PriceDiffTable = ({ title, rows = [], changed = false }) => {
  if (!rows.length) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Price Unit</TableCell>
            {changed ? (
              <>
                <TableCell>Old Qty</TableCell>
                <TableCell>New Qty</TableCell>
                <TableCell>Old Rate</TableCell>
                <TableCell>New Rate</TableCell>
              </>
            ) : (
              <>
                <TableCell>Quantity</TableCell>
                <TableCell>Rate</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.key}>
              <TableCell>{row.price_unit}</TableCell>
              {changed ? (
                <>
                  <TableCell>{formatQty(row.old_quantity)}</TableCell>
                  <TableCell>{formatQty(row.new_quantity)}</TableCell>
                  <TableCell>{formatRate(row.old_rate)}</TableCell>
                  <TableCell>{formatRate(row.new_rate)}</TableCell>
                </>
              ) : (
                <>
                  <TableCell>{formatQty(row.quantity)}</TableCell>
                  <TableCell>{formatRate(row.rate)}</TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const LinguistRateRequests = () => {
  const dispatch = useDispatch();
  const handleSmartNavigation = useHandleSmartNavigation();
  const [status, setStatus] = useState("pending");
  const [batchRows, setBatchRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openReviewPopup, setOpenReviewPopup] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [comment, setComment] = useState("");

  const fetchRows = () => {
    setLoading(true);
    authApi
      .get(`/admin/price-list-draft-requests`, { params: { status } })
      .then(res => {
        setBatchRows(Array.isArray(res?.data?.data) ? res.data.data : []);
      })
      .catch(err => {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.msg || "Failed to load draft requests.",
            type: "error",
          })
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
  }, [status]);

  const openReview = requestRow => {
    setOpenReviewPopup(true);
    setSelectedRequest(null);
    setComment(requestRow?.comment || "");
    setReviewLoading(true);

    authApi
      .get(`/admin/price-list-draft-requests/${requestRow.id}`)
      .then(res => {
        setSelectedRequest(res?.data?.data || null);
      })
      .catch(err => {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.msg || "Failed to load request details.",
            type: "error",
          })
        );
        setOpenReviewPopup(false);
      })
      .finally(() => setReviewLoading(false));
  };

  const handleBatchAction = async action => {
    if (!selectedRequest?.id) return;
    setProcessing(true);

    try {
      const endpoint =
        action === "approve"
          ? `/admin/price-list-draft-requests/${selectedRequest.id}/approve`
          : `/admin/price-list-draft-requests/${selectedRequest.id}/reject`;

      await authApi.post(endpoint, { comment: comment || null });
      dispatch(
        showSnackbar({
          message: action === "approve" ? "Approved." : "Rejected.",
          type: "success",
        })
      );
      setOpenReviewPopup(false);
      setSelectedRequest(null);
      fetchRows();
    } catch (err) {
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Action failed.",
          type: "error",
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  const batchColumns = useMemo(
    () => [
      {
        name: "rowNumber",
        label: "#",
        options: {
          sort: false,
          customBodyRenderLite: dataIndex => dataIndex + 1,
        },
      },
      { name: "id", label: "ID", options: { display: false } },
      {
        name: "vUserName",
        label: "Linguist",
        options: {
          customBodyRender: (_, tableMeta) => {
            const row = batchRows?.[tableMeta.rowIndex];
            const fullName = `${row?.vFirstName || ""} ${row?.vLastName || ""}`.trim();
            return (
              <Typography variant="body2" fontWeight={500}>
                {fullName || row?.vUserName || "-"}
              </Typography>
            );
          },
        },
      },
      {
        name: "price_name",
        label: "Price List",
        options: {
          customBodyRender: (value, tableMeta) => {
            const row = batchRows?.[tableMeta.rowIndex];
            const linguistId = row?.linguist_id;
            const displayName = value || row?.price_list_id || "-";
            if (!linguistId) {
              return displayName;
            }
            return (
              <Typography
                variant="body2"
                onClick={e =>
                  handleSmartNavigation(
                    e,
                    `/manage/external/${linguistId}?step=2&price_list_id=${row?.price_list_id}`
                  )
                }
                sx={{ cursor: "pointer", color: "primary.main", fontWeight: 500 }}
              >
                {displayName}
              </Typography>
            );
          },
        },
      },
      // {
      //   name: "summary",
      //   label: "Summary",
      //   options: {
      //     sort: false,
      //     customBodyRender: (_, tableMeta) => <SummaryBlock summary={batchRows?.[tableMeta.rowIndex]?.summary} />,
      //   },
      // },
      {
        name: "status",
        label: "Status",
        options: {
          customBodyRender: value => statusChip(value),
        },
      },
      {
        name: "created_at",
        label: "Created At",
        options: {
          customBodyRender: value => formatDate(value, { includeTime: true }) || "-",
        },
      },
      {
        name: "actions",
        label: "Actions",
        options: {
          sort: false,
          filter: false,
          customBodyRenderLite: dataIndex => {
            const row = batchRows?.[dataIndex];
            return (
              <Button size="small" variant="outlined" onClick={() => openReview(row)}>
                Review
              </Button>
            );
          },
        },
      },
    ],
    [batchRows, handleSmartNavigation]
  );

  return (
    <Box display="grid" gap={3}>
      <Card>
        <CardHeader
          title="Price List Draft Requests"
          action={
            <Box display="flex" gap={2} alignItems="center">
              <DropdownComponent
                label="Status"
                name="status"
                value={status}
                onChange={e => setStatus(e.target.value)}
                options={statusOptions}
                searchable={false}
                isNotMandatory
                sx={{ minWidth: 200 }}
              />
            </Box>
          }
        />
        <CardContent sx={{ padding: "0px!important" }}>
          <GenericTable
            columns={batchColumns}
            data={batchRows}
            showAction={false}
            isTableLoading={loading}
            options={{
              selectableRows: "none",
              search: true,
              download: false,
              print: false,
              filter: false,
              viewColumns: false,
            }}
          />
        </CardContent>
      </Card>

      <PopupModal
        open={openReviewPopup}
        width="900px"
        handleClose={() => setOpenReviewPopup(false)}
        title={<Typography variant="h6">Review draft request</Typography>}
        actions={
          <Grid container columnGap={2} justifyContent={"end"}>
            {selectedRequest?.status === "pending" && (
              <>
                <Btn onClick={() => handleBatchAction("approve")} disabled={processing || reviewLoading}>
                  Approve
                </Btn>
                <Btn
                  variant="outlined"
                  onClick={() => handleBatchAction("reject")}
                  disabled={processing || reviewLoading}
                >
                  Reject
                </Btn>
              </>
            )}
            <Btn variant="outlined" onClick={() => setOpenReviewPopup(false)} disabled={processing}>
              Close
            </Btn>
          </Grid>
        }
        maxWidth={"md"}
        fullWidth={true}
      >
        {reviewLoading ? (
          <Typography>Loading request details...</Typography>
        ) : selectedRequest ? (
          <Box>
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Box>
                <Typography variant="subtitle1">{selectedRequest.price_name || "-"}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {(selectedRequest.vFirstName || selectedRequest.vLastName
                    ? `${selectedRequest.vFirstName || ""} ${selectedRequest.vLastName || ""}`.trim()
                    : selectedRequest.vUserName) || "-"}
                </Typography>
              </Box>
              <Box>{statusChip(selectedRequest.status)}</Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Summary
              </Typography>
              <SummaryBlock summary={selectedRequest.summary} />
            </Box>

            {(selectedRequest?.diff?.languages?.added?.length > 0 ||
              selectedRequest?.diff?.languages?.removed?.length > 0) && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Language Pair Changes
                </Typography>
                {selectedRequest?.diff?.languages?.added?.length > 0 && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Added: {selectedRequest.diff.languages.added.join(", ")}
                  </Typography>
                )}
                {selectedRequest?.diff?.languages?.removed?.length > 0 && (
                  <Typography variant="body2">
                    Removed: {selectedRequest.diff.languages.removed.join(", ")}
                  </Typography>
                )}
              </Box>
            )}

            <PriceDiffTable
              title="Changed Existing Rows"
              rows={selectedRequest?.diff?.prices?.changed || []}
              changed
            />
            <PriceDiffTable title="Added Service Rows" rows={selectedRequest?.diff?.prices?.added || []} />
            <PriceDiffTable
              title="Removed Service Rows"
              rows={selectedRequest?.diff?.prices?.removed || []}
            />

            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Comment (optional)"
                value={comment}
                onChange={e => setComment(e.target.value)}
                multiline
                minRows={3}
              />
            </Box>
          </Box>
        ) : (
          <Typography>No request selected.</Typography>
        )}
      </PopupModal>
    </Box>
  );
};

export default LinguistRateRequests;

